import React, { useState, useCallback } from 'react';
import { Brain, Volume2, VolumeX, Play, RotateCcw } from 'lucide-react';
import { sampleFlashCardSets, flashCardSettings } from '../data/flashCardsData';
import deepseekApi from '../services/deepseekApi';

const FlashCardsFallback = ({ onBack }) => {
  const [selectedSet, setSelectedSet] = useState(null);
  const [step, setStep] = useState('select'); // select, playing, results

  const handleSelectSet = (set) => {
    setSelectedSet(set);
    setStep('playing');
  };

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div
        className="relative rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(147, 51, 234, 0.9), rgba(219, 39, 119, 0.9)), url(https://images.unsplash.com/photo-1606092195730-5d7b9af1efc5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-8 h-8" />
            <h2 className="text-3xl font-bold">學習卡片</h2>
          </div>
          <p className="text-purple-100 text-lg">
            使用我們的範例學習卡片組進行練習（AI 生成暫時無法使用）
          </p>
        </div>
      </div>

      {step === 'select' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900">選擇學習卡片組</h3>
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← 返回範本
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sampleFlashCardSets.map(set => (
              <div key={set.id} className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{set.title}</h4>
                    <p className="text-gray-600 text-sm mb-3">{set.description}</p>
                  </div>
                  <span className="text-2xl">🧠</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                  <div>
                    <span className="text-gray-500">卡片：</span>
                    <span className="font-medium text-gray-900 ml-1">{set.totalCards}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">語言：</span>
                    <span className="font-medium text-gray-900 ml-1">{set.language}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">難度：</span>
                    <span className="font-medium text-gray-900 ml-1 capitalize">{set.difficulty}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">類別：</span>
                    <span className="font-medium text-gray-900 ml-1 capitalize">{set.category}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleSelectSet(set)}
                  className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Play className="w-4 h-4" />
                  開始學習
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 'playing' && selectedSet && (
        <FlashCardsPlayer
          flashCards={selectedSet}
          onBack={() => setStep('select')}
          onComplete={() => setStep('results')}
        />
      )}

      {step === 'results' && selectedSet && (
        <ResultsSummary
          flashCards={selectedSet}
          onRestart={() => setStep('select')}
          onPlayAgain={() => setStep('playing')}
        />
      )}
    </div>
  );
};

const FlashCardsPlayer = ({ flashCards, onBack, onComplete }) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoAdvance, setAutoAdvance] = useState(false);

  const currentCard = flashCards.cards[currentCardIndex];
  const isLastCard = currentCardIndex === flashCards.cards.length - 1;
  const progress = ((currentCardIndex + 1) / flashCards.cards.length) * 100;

  const speakText = useCallback((text) => {
    if (soundEnabled) {
      deepseekApi.speakText(text, flashCards.language);
    }
  }, [soundEnabled, flashCards.language]);

  const handleNext = () => {
    if (isLastCard) {
      onComplete();
    } else {
      setCurrentCardIndex(prev => prev + 1);
      setShowAnswer(false);
    }
  };

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(prev => prev - 1);
      setShowAnswer(false);
    }
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
    if (autoAdvance) {
      setTimeout(() => {
        handleNext();
      }, flashCardSettings.autoAdvanceDelay);
    }
  };

  React.useEffect(() => {
    if (currentCard && soundEnabled) {
      speakText(currentCard.question);
    }
  }, [currentCardIndex, currentCard, soundEnabled, speakText]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← 返回選擇
            </button>
            <div className="text-sm text-gray-600">
              第 {currentCardIndex + 1} 張，共 {flashCards.cards.length} 張
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded-lg transition-colors ${
                soundEnabled 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            
            <button
              onClick={() => setAutoAdvance(!autoAdvance)}
              className={`p-2 rounded-lg transition-colors ${
                autoAdvance 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              <Play className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Flash Card Display */}
      <div className="bg-white rounded-xl shadow-lg border p-8 min-h-96">
        <div className="text-center space-y-6">
          <div className="bg-purple-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-purple-900 mb-4">
              {showAnswer ? '答案' : '問題'}
            </h3>
            <p className="text-xl text-gray-800 leading-relaxed">
              {showAnswer ? currentCard.answer : currentCard.question}
            </p>
          </div>
          
          {showAnswer && currentCard.explanation && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">說明</h4>
              <p className="text-sm text-blue-800">{currentCard.explanation}</p>
            </div>
          )}
          
          {!showAnswer && (
            <button
              onClick={handleShowAnswer}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              顯示答案
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePrevious}
          disabled={currentCardIndex === 0}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          上一張
        </button>
        
        {showAnswer && (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            {isLastCard ? '完成' : '下一張'}
          </button>
        )}
      </div>
    </div>
  );
};

const ResultsSummary = ({ flashCards, onRestart, onPlayAgain }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Brain className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          恭喜您！
        </h3>
        <p className="text-gray-600">
          您已完成所有 {flashCards.totalCards} 張關於 "{flashCards.title}" 的學習卡片
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{flashCards.totalCards}</div>
          <div className="text-sm text-gray-500">已完成卡片</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{flashCards.language}</div>
          <div className="text-sm text-gray-500">語言</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{flashCards.difficulty}</div>
          <div className="text-sm text-gray-500">難度</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">100%</div>
          <div className="text-sm text-gray-500">進度</div>
        </div>
      </div>

      <div className="flex gap-4 justify-center">
        <button
          onClick={onPlayAgain}
          className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          再次練習
        </button>
        <button
          onClick={onRestart}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Brain className="w-4 h-4" />
          選擇其他組合
        </button>
      </div>
    </div>
  );
};

export default FlashCardsFallback; 